import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, CircularProgress, Pagination,
  Avatar, TextField, MenuItem, Button, Dialog, DialogActions,
  DialogContent, DialogTitle, IconButton, Checkbox, Tooltip,
  Snackbar, Drawer, Alert, Autocomplete,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTheme, useMediaQuery } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import moment from 'moment';

import CheckIcon                      from '@mui/icons-material/Check';
import EditNoteIcon                   from '@mui/icons-material/EditNote';
import AssignmentTurnedInIcon         from '@mui/icons-material/AssignmentTurnedIn';
import TransferWithinAStationIcon     from '@mui/icons-material/TransferWithinAStation';
import SevereColdIcon                 from '@mui/icons-material/SevereCold';
import FilterListIcon                 from '@mui/icons-material/FilterList';
import RefreshIcon                    from '@mui/icons-material/Refresh';
import SettingsIcon                   from '@mui/icons-material/Settings';
import AddIcon                        from '@mui/icons-material/Add';
import CloseIcon                      from '@mui/icons-material/Close';
import VisibilityIcon                 from '@mui/icons-material/Visibility';
import VisibilityOffIcon              from '@mui/icons-material/VisibilityOff';
import ReplyIcon                      from '@mui/icons-material/Reply';
import ArrowDownwardIcon              from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon                from '@mui/icons-material/ArrowUpward';
import ClearIcon                      from '@mui/icons-material/Clear';
import ContentCopyIcon                from '@mui/icons-material/ContentCopy';
import OpenInNewIcon                  from '@mui/icons-material/OpenInNew';
import AssignmentIcon                 from '@mui/icons-material/Assignment';
import CalendarViewWeekOutlinedIcon   from '@mui/icons-material/CalendarViewWeekOutlined';

import { GetallLeads_data_With_Email2 }                                             from '../Redux/Actions/RoleData_Actions';
import { UpdateClientAction, UpdateClientActionToReaciveusertolead }                from '../Redux/Actions/UsersAction';
import { GetallProjectsdata }                                                       from '../Redux/Actions/Projects_Action';
import { Get_sales_Login_For_Assign_Leads_To_histeamleader_Action }                from '../Redux/Actions/SalesAction';
import { Notify }                                                                   from '../Utility/Notify';
import { ShowClientsActionsAdvanced }                                               from './ShowClientsActionsAdvanced';
import { ShowUserSheet }                                                            from './ShowUserSheet';
import { PrivateSheetForUser }                                                      from './PrivateSheetForUser';
import SalesQuickFilterModal                                                        from './SalesQuickFilterModal';
import { useSidebarWidth }                                                          from '../Utility/useSidebarWidth';

// ─── Tokens ───────────────────────────────────────────────────────────────────
const C = {
  bg:          '#F7F8FC',
  white:       '#FFFFFF',
  border:      '#E8EAF0',
  accent:      '#0D47A1',
  accentDark:  '#1330A6',
  accentSoft:  'rgba(26,62,207,0.07)',
  text:        '#1C2340',
  textMuted:   '#7B829A',
  success:     '#12B76A',
  successSoft: 'rgba(18,183,106,0.08)',
  warning:     '#F79009',
  danger:      '#F04438',
  dangerSoft:  'rgba(240,68,56,0.08)',
  shadow:      '0 1px 3px rgba(16,24,40,0.08)',
};

const SIDEBAR_COLLAPSED = 64;

// ─── Styled row ───────────────────────────────────────────────────────────────
const StyledRow = styled(TableRow)(() => ({
  '&:hover': { backgroundColor: 'rgba(26,62,207,0.03)' },
  '& td':    { borderBottom: `1px solid ${C.border}` },
}));

// ─── Stat card ────────────────────────────────────────────────────────────────
const StatCard = ({ label, value, highlight }) => (
  <Box sx={{
    bgcolor:      highlight ? C.accent : C.white,
    border:       `1px solid ${highlight ? C.accent : C.border}`,
    borderRadius: '12px', p: 2.5,
    width: 160, minWidth: 160, height: 120, minHeight: 120,
    boxShadow: highlight ? '0 4px 16px rgba(26,62,207,0.25)' : C.shadow,
    position: 'relative', overflow: 'hidden', flexShrink: 0,
    display: 'flex', flexDirection: 'column', justifyContent: 'space-between',
  }}>
    <Typography sx={{ fontSize:'11px', fontWeight:500, color: highlight ? 'rgba(255,255,255,0.75)' : C.textMuted, letterSpacing:'0.06em', textTransform:'uppercase', fontFamily:"'DM Sans',sans-serif" }}>
      {label}
    </Typography>
    <Typography sx={{ fontSize:'26px', fontWeight:700, color: highlight ? '#fff' : C.text, fontFamily:"'DM Sans',sans-serif", lineHeight:1.2 }}>
      {value != null ? String(value) : '—'}
    </Typography>
    {highlight && <Box sx={{ height:'3px', width:'40px', bgcolor:'#F79009', borderRadius:'2px' }} />}
  </Box>
);

// ─── Action button ────────────────────────────────────────────────────────────
const ActionBtn = ({ icon, label, onClick, primary, disabled }) => (
  <Button onClick={onClick} disabled={disabled} startIcon={icon} size="small"
    sx={{
      fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:'13px',
      borderRadius:'8px', textTransform:'none', px:'14px', py:'7px',
      ...(primary
        ? { bgcolor:C.accent, color:'#fff', border:'none', boxShadow:'0 2px 8px rgba(26,62,207,0.25)', '&:hover':{ bgcolor:C.accentDark } }
        : { bgcolor:C.white, color:C.text, border:`1px solid ${C.border}`, '&:hover':{ bgcolor:C.bg, borderColor:C.accent, color:C.accent } }
      ),
    }}
  >
    {label}
  </Button>
);

// ─── Phone cell ───────────────────────────────────────────────────────────────
const PhoneCell = ({ user }) => {
  const [show, setShow] = useState(false);
  return (
    <Box onMouseEnter={() => setShow(true)} onMouseLeave={() => setShow(false)}>
      <Typography sx={{ fontSize:'13px', color:C.text, mb:'4px' }}>
        {show ? user.phone : `${(user.phone||'').substring(0,6)}···`}
      </Typography>
      <Box sx={{ display:'flex', gap:'6px' }}>
        <IconButton size="small"
          onClick={() => window.open(`https://wa.me/${(user.phone||'').replace(/\D/g,'')}`, '_blank')}
          sx={{ width:26, height:26, bgcolor:'rgba(37,211,102,0.1)', color:'#25D366', '&:hover':{ bgcolor:'rgba(37,211,102,0.2)' } }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        </IconButton>
        <IconButton size="small"
          onClick={() => window.open(`tel:${user.phone}`)}
          sx={{ width:26, height:26, bgcolor:'rgba(26,62,207,0.08)', color:C.accent, '&:hover':{ bgcolor:'rgba(26,62,207,0.15)' } }}>
          <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z"/>
          </svg>
        </IconButton>
      </Box>
    </Box>
  );
};

// ─── Stage cell — exact same logic as Admin ───────────────────────────────────
const StageCell = ({ lead }) => {
  const stageName  = lead.stage?.name || '';
  const isOverdue  = lead.stagedateupdated && moment(lead.stagedateupdated).isBefore(moment());
  const circleColor = isOverdue ? '#CC5500' : C.accent;

  // badge bg + text color per stage name
  const getBadgeStyle = (name) => {
    switch (name) {
      case 'Done Deal':       return { bg: 'rgba(18,183,106,0.10)',  color: '#12B76A' };
      case 'Pending':         return { bg: 'rgba(156,39,176,0.08)', color: '#9c27b0' };
      case 'Transfer':        return { bg: 'rgba(240,68,56,0.08)',  color: '#F04438' };
      case 'Not Interested':  return { bg: 'rgba(85,85,85,0.08)',   color: '#555555' };
      case 'Fresh':           return { bg: 'rgba(76,175,80,0.08)',  color: '#4caf50' };
      case 'EOI':             return { bg: 'rgba(156,39,176,0.08)', color: '#9c27b0' };
      case 'Reservation':     return { bg: 'rgba(156,39,176,0.08)', color: '#9c27b0' };
      case 'No Answer':       return { bg: isOverdue ? 'rgba(204,85,0,0.10)' : 'rgba(255,107,53,0.08)', color: isOverdue ? '#CC5500' : '#FF6B35' };
      default:                return { bg: isOverdue ? 'rgba(204,85,0,0.10)' : C.accentSoft, color: isOverdue ? '#CC5500' : C.accent };
    }
  };

  const badge = getBadgeStyle(stageName);

  const renderIcon = () => {
    switch (stageName) {
      case 'Done Deal':       return <span style={{ fontSize:'18px', lineHeight:1 }}>🎉</span>;
      case 'Pending':         return <SevereColdIcon sx={{ color:'#9c27b0', fontSize:'18px', flexShrink:0 }} />;
      case 'Fresh':           return <CheckIcon sx={{ fontSize:'18px', color:'#4caf50', flexShrink:0 }} />;
      case 'Not Interested':  return (
        <Box sx={{ width:18, height:18, borderRadius:'50%', bgcolor:'#555', display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', fontSize:'12px', fontWeight:700, flexShrink:0, lineHeight:1 }}>—</Box>
      );
      case 'EOI':             return <EditNoteIcon sx={{ color:'#9c27b0', fontSize:'18px', flexShrink:0 }} />;
      case 'Reservation':     return <AssignmentTurnedInIcon sx={{ color:'#9c27b0', fontSize:'18px', flexShrink:0 }} />;
      case 'Transfer':        return <TransferWithinAStationIcon sx={{ color:'#F04438', fontSize:'18px', flexShrink:0 }} />;
      case 'No Answer':       return <Box sx={{ width:18, height:18, borderRadius:'50%', bgcolor:circleColor, flexShrink:0 }} />;
      default:                return <Box sx={{ width:18, height:18, borderRadius:'50%', bgcolor:circleColor, flexShrink:0 }} />;
    }
  };

  if (!stageName) return null;

  return (
    <Box>
      <Box sx={{ display:'flex', alignItems:'center', gap:1, mb: lead.stagedateupdated ? '4px' : 0 }}>
        {renderIcon()}
        <Box sx={{ display:'inline-flex', alignItems:'center', px:'8px', py:'3px', borderRadius:'20px', bgcolor:badge.bg, color:badge.color }}>
          <Typography sx={{ fontSize:'11.5px', fontWeight:600, fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' }}>
            {stageName}
          </Typography>
        </Box>
      </Box>

      {lead.stagedateupdated && (
        <Typography sx={{ fontSize:'10.5px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif", display:'flex', alignItems:'center', gap:'3px' }}>
          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
          </svg>
          {moment(lead.stagedateupdated).format('DD/MM/YY HH:mm')}
        </Typography>
      )}
    </Box>
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const getInitials = (n='') =>
  (n||'').split(' ').filter(Boolean).map(w=>w[0]).join('').toUpperCase().slice(0,2)||'?';

// ─── Default columns ──────────────────────────────────────────────────────────
const DEFAULT_COLS = [
  { key:'name',      label:'Name',          w:200, visible:true },
  { key:'phone',     label:'Phone',         w:140, visible:true },
  { key:'project',   label:'Project',       w:170, visible:true },
  { key:'campaign',  label:'Campaign',      w:150, visible:true },
  { key:'stage',     label:'Stage',         w:200, visible:true },
  { key:'createdAt', label:'Creation Date', w:160, visible:true },
  { key:'actions',   label:'Actions',       w:260, visible:true },
];

// ─── Main ─────────────────────────────────────────────────────────────────────
export const All_Leads_For_Sales_Page = () => {
  const isMobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const dispatch  = useDispatch();
  const location  = useLocation();
  const sidebarW  = useSidebarWidth();

  const leadsFromRedux = useSelector(s => s.AllRoleData.LeadsForSales);
  const resproject     = useSelector(s => s.AllProjects.Projects);
  const salesdata      = useSelector(s => s.AllSales.sales_Assign_Leads_To_hisTeamLeader);
  const resupdatelead  = useSelector(s => s.Allusers.Get_users);

  // ── state ──────────────────────────────────────────────────────────────────
  const [userlogin,     setUserlogin]     = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [currentPage,   setCurrentPage]   = useState(1);
  const [itemsPerPage,  setItemsPerPage]  = useState(25);
  const [searchQuery,   setSearchQuery]   = useState(localStorage.getItem('searchsalesuserstorage')||'');
  const [selectedLeads, setSelectedLeads] = useState([]);
  const [selectedLead,  setSelectedLead]  = useState(null);
  const [appliedFilters,setAppliedFilters]= useState({});
  const [sortConfig,    setSortConfig]    = useState({ key:null, direction:'ascending' });

  const [colConfig, setColConfig] = useState(() => {
    try { const s = localStorage.getItem('salesColConfig'); return s ? JSON.parse(s) : DEFAULT_COLS; } catch { return DEFAULT_COLS; }
  });
  useEffect(() => { localStorage.setItem('salesColConfig', JSON.stringify(colConfig)); }, [colConfig]);

  // modals
  const [quickFilter,          setQuickFilter]          = useState(false);
  const [settingsOpen,         setSettingsOpen]         = useState(false);
  const [sheetOpen,            setSheetOpen]            = useState(false);
  const [privateSheetOpen,     setPrivateSheetOpen]     = useState(false);
  const [selectedLeadforsheet, setSelectedLeadforsheet] = useState(null);
  const [showReceiveModal,     setShowReceiveModal]     = useState(false);
  const [selectedLeadForRcv,   setSelectedLeadForRcv]   = useState(null);
  const [receiveLoading,       setReceiveLoading]       = useState(false);
  const [confirmReturn,        setConfirmReturn]        = useState(false);
  const [loadingReturn,        setLoadingReturn]        = useState(true);
  const [showUpdateLead,       setShowUpdateLead]       = useState(false);
  const [selectedLeadId,       setSelectedLeadId]       = useState(null);
  const [leadnameupdate,       setLeadnameupdate]       = useState('');
  const [phonenumber2,         setPhonenumber2]         = useState('');
  const [whatsappnumber,       setWhatsappnumber]       = useState('');
  const [projectupdatelead,    setProjectupdatelead]    = useState(null);
  const [loadingUpdate,        setLoadingUpdate]        = useState(true);
  const [loadingUpdate2,       setLoadingUpdate2]       = useState(false);

  useEffect(() => { const u = localStorage.getItem('user'); if (u) setUserlogin(JSON.parse(u)); }, []);

  // ── col helpers ──────────────────────────────────────────────────────────
  const toggleColVisibility = (key) => setColConfig(p => p.map(c => c.key===key ? {...c,visible:!c.visible} : c));
  const updateColWidth = (key,w) => setColConfig(p => p.map(c => c.key===key ? {...c,w:Math.max(60,Math.min(400,parseInt(w)||c.w))} : c));
  const onDragEnd = (result) => {
    if (!result.destination) return;
    const items = Array.from(colConfig);
    const [moved] = items.splice(result.source.index,1);
    items.splice(result.destination.index,0,moved);
    setColConfig(items);
  };

  // ── data helpers ─────────────────────────────────────────────────────────
  const getLeadsArray = () => {
    if (!leadsFromRedux) return [];
    if (leadsFromRedux.data?.data && Array.isArray(leadsFromRedux.data.data)) return leadsFromRedux.data.data;
    if (leadsFromRedux.data && Array.isArray(leadsFromRedux.data)) return leadsFromRedux.data;
    if (Array.isArray(leadsFromRedux)) return leadsFromRedux;
    return [];
  };
  const getPagination = () => {
    if (!leadsFromRedux) return null;
    if (leadsFromRedux.data?.pagination) return leadsFromRedux.data.pagination;
    if (leadsFromRedux.pagination) return leadsFromRedux.pagination;
    return null;
  };

  // ── fetch ─────────────────────────────────────────────────────────────────
  const loadLeads = async (page=1, limit=itemsPerPage, search=searchQuery, filters=appliedFilters) => {
    if (!userlogin?.data?.email) return;
    setLoading(true);

     let processedSearchTerm = search;
  if (search && search.includes('+')) {
    processedSearchTerm = search.replace('+', '');
  }
    let q = `page=${page}&limit=${limit}&email=${encodeURIComponent(userlogin.data.email)}&data=false&transferefromdata=true`;
    const sp = new URLSearchParams(location.search);
    if (sp.get('stage')) q += `&stage=${sp.get('stage')}`;
  if (processedSearchTerm?.trim()) q += `&keyword=${encodeURIComponent(processedSearchTerm.trim())}`;
    
    // if (filters.stage?.length)    filters.stage.forEach(s => { if (s?._id) q += `&stage=${s._id}`; });
    // if (filters.project?.length)  filters.project.forEach(p => { if (p?._id) q += `&project=${p._id}`; });
    // if (filters.campaign?.length) filters.campaign.forEach(c => { if (c?._id) q += `&campaign=${c._id}`; });
    // if (filters.createdAt?.from)  q += `&createdFrom=${encodeURIComponent(filters.createdAt.from)}`;
    
    // if (filters.createdAt?.to)    q += `&createdTo=${encodeURIComponent(filters.createdAt.to)}`;
    if (filters.stage?.length)    filters.stage.forEach(s => { if (s?._id) q += `&stage=${s._id}`; });
    if (filters.project?.length)  filters.project.forEach(p => { if (p?._id) q += `&project=${p._id}`; });
    if (filters.developer?.length) filters.developer.forEach(d => { if (d?._id) q += `&developer=${d._id}`; });
    if (filters.chanel?.length)   filters.chanel.forEach(c => { if (c?._id) q += `&channel=${c._id}`; });
    if (filters.campaign?.length) filters.campaign.forEach(c => { if (c?._id) q += `&campaign=${c._id}`; });
    if (filters.createdAt?.from)  q += `&createdFrom=${encodeURIComponent(filters.createdAt.from)}`;
    if (filters.createdAt?.to)    q += `&createdTo=${encodeURIComponent(filters.createdAt.to)}`;
    if (filters.stagedateupdated?.from) q += `&stageDateFrom=${encodeURIComponent(filters.stagedateupdated.from)}`;
    if (filters.stagedateupdated?.to)   q += `&stageDateTo=${encodeURIComponent(filters.stagedateupdated.to)}`;
    try { await dispatch(GetallLeads_data_With_Email2(userlogin.data.email, q)); }
    catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (userlogin?.data?.email) { loadLeads(); dispatch(GetallProjectsdata()); } }, [userlogin]);

  useEffect(() => {
    const handler = () => {
      const v = localStorage.getItem('searchsalesuserstorage')||'';
      setSearchQuery(v); setCurrentPage(1);
      loadLeads(1, itemsPerPage, v);
    };
    window.addEventListener('searchsalesuserstorageUpdate', handler);
    return () => window.removeEventListener('searchsalesuserstorageUpdate', handler);
  }, [userlogin, itemsPerPage]);

  // ── sort ──────────────────────────────────────────────────────────────────
  const displayLeads = useMemo(() => {
    const arr = getLeadsArray();
    if (!arr.length || !sortConfig.key) return arr;
    return [...arr].sort((a,b) => {
      const get = l => sortConfig.key==='createdAt' ? new Date(l.date||0) : new Date(l.stagedateupdated||0);
      const va=get(a), vb=get(b);
      return sortConfig.direction==='ascending' ? va-vb : vb-va;
    });
  }, [leadsFromRedux, sortConfig]);

  const pagination = getPagination();

  // ── selection ─────────────────────────────────────────────────────────────
  const toggleLead = (id) => setSelectedLeads(p => p.includes(id)?p.filter(x=>x!==id):[...p,id]);
  const toggleAll  = () => {
    if (selectedLeads.length===displayLeads.length) setSelectedLeads([]);
    else setSelectedLeads(displayLeads.map(l=>l._id));
  };

  // ── receive ───────────────────────────────────────────────────────────────
  const handleReceiveLead = async () => {
    if (!selectedLeadForRcv) return;
    setReceiveLoading(true);
    try {
      await dispatch(UpdateClientActionToReaciveusertolead(selectedLeadForRcv._id, { assign:false }));
      Notify('success','Lead received successfully','success');
      setShowReceiveModal(false); setSelectedLeadForRcv(null);
      loadLeads(currentPage, itemsPerPage);
    } catch { Notify('error','Failed to receive lead','error'); }
    finally { setReceiveLoading(false); }
  };

  // ── return to TL ──────────────────────────────────────────────────────────
  const handleReturnToTL = () => { if (!selectedLeads.length) return; setConfirmReturn(true); };
  const executeReturn = async () => {
    setLoadingReturn(true);
    try {
      await dispatch(Get_sales_Login_For_Assign_Leads_To_histeamleader_Action(selectedLeads, false));
      await loadLeads();
      setConfirmReturn(false);
    } catch { Notify('error','Failed to return leads','error'); }
    finally { setLoadingReturn(false); }
  };
  useEffect(() => {
    if (!loadingReturn && salesdata?.status===200) {
      Notify('success','Leads returned to Team Leader','success');
      setSelectedLeads([]);
    }
  }, [loadingReturn]);

  // ── edit lead ─────────────────────────────────────────────────────────────
  const handleShowUpdateLead = (lead) => {
    setSelectedLeadId(lead._id);
    setLeadnameupdate(lead.name||'');
    setPhonenumber2(lead.phonenumber2||'');
    setWhatsappnumber(lead.whatsappnumber||'');
    setProjectupdatelead(lead.project ? (resproject?.data?.find(p=>p._id===lead.project._id)||lead.project) : null);
    setShowUpdateLead(true);
  };
  const handleUpdateLead = async (e) => {
    e.preventDefault();
    if (!selectedLeadId||!leadnameupdate) return;
    setLoadingUpdate(true); setLoadingUpdate2(true);
    try { await dispatch(UpdateClientAction(selectedLeadId, { name:leadnameupdate, phonenumber2, whatsappnumber, project:projectupdatelead })); }
    catch { Notify('error','Failed to update lead','error'); }
    finally { setLoadingUpdate(false); setLoadingUpdate2(false); }
  };
  useEffect(() => {
    if (!loadingUpdate && resupdatelead?.status===200) {
      Notify('success','Lead updated successfully','success');
      setShowUpdateLead(false);
      loadLeads();
    }
  }, [loadingUpdate]);

  const refresh = () => { setCurrentPage(1); setSelectedLeads([]); loadLeads(1, itemsPerPage); };

  // ─── JSX ──────────────────────────────────────────────────────────────────
  return (
    <LocalizationProvider dateAdapter={AdapterMoment}>
      <Box sx={{ ml:isMobile ? `${SIDEBAR_COLLAPSED}px` : `${sidebarW}px`, mt:'64px', minHeight:'calc(100vh - 64px)', bgcolor:C.bg, transition:'margin-left 0.25s cubic-bezier(0.4,0,0.2,1)' }}>
        <Box sx={{ p:isMobile ? 2 : 3.5 }}>

          {/* Header */}
          <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:2, mb:3 }}>
            <Box>
              <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:800, fontSize:isMobile ? '28px' : '36px', color:C.text, lineHeight:1.15, letterSpacing:'-0.02em' }}>
                My Leads<br />Center
              </Typography>
              <Typography sx={{ fontSize:'13.5px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif", mt:1 }}>
                Welcome back, <strong style={{ color:C.text }}>{userlogin?.data?.name||'Sales'}</strong> — manage and track your assigned leads.
              </Typography>
            </Box>
            <Box sx={{ display:'flex', gap:'12px', flexWrap:'wrap', alignItems:'stretch' }}>
              <StatCard label="Total Leads"  value={pagination?.totalItems} />
              <StatCard label="Pages"        value={pagination?.NumberOfPages} />
              <StatCard label="Current Page" value={currentPage} />
              <StatCard label="Per Page"     value={itemsPerPage} highlight />
            </Box>
          </Box>

          {/* Table card */}
          <Box sx={{ bgcolor:C.white, borderRadius:'12px', border:`1px solid ${C.border}`, boxShadow:C.shadow, overflow:'hidden' }}>

            {/* Toolbar */}
            <Box sx={{ px:2.5, py:2, display:'flex', alignItems:'center', gap:1.5, flexWrap:'wrap', borderBottom:`1px solid ${C.border}` }}>
              <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:'16px', color:C.text, flex:1 }}>Live Pipeline</Typography>

              {/* Sort */}
              <Tooltip title="Creation Date Newest first">
                <IconButton size="small" onClick={() => setSortConfig({ key:'createdAt', direction:'descending' })}
                  sx={{ color: sortConfig.key==='createdAt'&&sortConfig.direction==='descending' ? C.accent : C.textMuted, '&:hover':{ color:C.accent } }}>
                  <ArrowDownwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Creation Date Oldest first">
                <IconButton size="small" onClick={() => setSortConfig({ key:'createdAt', direction:'ascending' })}
                  sx={{ color: sortConfig.key==='createdAt'&&sortConfig.direction==='ascending' ? C.accent : C.textMuted, '&:hover':{ color:C.accent } }}>
                  <ArrowUpwardIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              {sortConfig.key && (
                <Tooltip title="Clear sort">
                  <IconButton size="small" onClick={() => setSortConfig({ key:null, direction:'ascending' })} sx={{ color:C.textMuted,'&:hover':{ color:C.danger } }}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}



<Box sx={{ width:'1px', height:'20px', bgcolor:C.border }} />

<Tooltip title="Stage date newest first">
  <IconButton size="small" onClick={() => setSortConfig({ key:'stagedateupdated', direction:'descending' })}
    sx={{ color: sortConfig.key==='stagedateupdated'&&sortConfig.direction==='descending' ? C.accent : C.textMuted, '&:hover':{ color:C.accent } }}>
    <ArrowDownwardIcon fontSize="small" />
  </IconButton>
</Tooltip>
<Tooltip title="Stage date oldest first">
  <IconButton size="small" onClick={() => setSortConfig({ key:'stagedateupdated', direction:'ascending' })}
    sx={{ color: sortConfig.key==='stagedateupdated'&&sortConfig.direction==='ascending' ? C.accent : C.textMuted, '&:hover':{ color:C.accent } }}>
    <ArrowUpwardIcon fontSize="small" />
  </IconButton>
</Tooltip>


              <Box sx={{ width:'1px', height:'20px', bgcolor:C.border }} />

              <Tooltip title="Sheet view"><IconButton size="small" sx={{ color:C.textMuted,'&:hover':{ color:C.accent } }} onClick={() => setPrivateSheetOpen(true)}><CalendarViewWeekOutlinedIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Quick Filter"><IconButton size="small" sx={{ color:C.textMuted,'&:hover':{ color:C.accent } }} onClick={() => setQuickFilter(true)}><FilterListIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Refresh"><IconButton size="small" sx={{ color:C.textMuted,'&:hover':{ color:C.accent } }} onClick={refresh} disabled={loading}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Table settings"><IconButton size="small" sx={{ color:C.textMuted,'&:hover':{ color:C.accent } }} onClick={() => setSettingsOpen(true)}><SettingsIcon fontSize="small" /></IconButton></Tooltip>

              <Box sx={{ width:'1px', height:'20px', bgcolor:C.border }} />
              <ActionBtn icon={<AddIcon />} label="New Lead" primary onClick={() => { window.location.hash='#/addclient'; }} />
              <ActionBtn icon={<AssignmentIcon />} label="Private Sheet" onClick={() => setPrivateSheetOpen(true)} />
            </Box>

            {/* Table */}
            <TableContainer sx={{ overflowX:'auto' }}>
              <Table size="small" sx={{ minWidth:700 }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width:52, minWidth:52, py:'10px', px:'12px', borderBottom:`2px solid ${C.border}` }}>
                      <Checkbox size="small"
                        checked={selectedLeads.length===displayLeads.length && displayLeads.length>0}
                        indeterminate={selectedLeads.length>0 && selectedLeads.length<displayLeads.length}
                        onChange={toggleAll}
                        sx={{ color:C.textMuted }}
                      />
                    </TableCell>
                    {colConfig.filter(c=>c.visible).map(col => (
                      <TableCell key={col.key} sx={{ width:col.w, minWidth:col.w, py:'10px', px:'12px', fontFamily:"'DM Sans',sans-serif", fontSize:'11.5px', fontWeight:600, color:C.textMuted, letterSpacing:'0.05em', textTransform:'uppercase', borderBottom:`2px solid ${C.border}`, whiteSpace:'nowrap' }}>
                        {col.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={colConfig.filter(c=>c.visible).length+1} align="center" sx={{ py:6 }}><CircularProgress size={32} sx={{ color:C.accent }} /></TableCell></TableRow>
                  ) : !displayLeads.length ? (
                    <TableRow><TableCell colSpan={colConfig.filter(c=>c.visible).length+1} align="center" sx={{ py:6 }}><Typography sx={{ color:C.textMuted, fontFamily:"'DM Sans',sans-serif" }}>No leads found</Typography></TableCell></TableRow>
                  ) : displayLeads.map(lead => (
                    <StyledRow key={lead._id}>
                      <TableCell sx={{ px:'12px', py:'10px' }}>
                        <Checkbox size="small" checked={selectedLeads.includes(lead._id)} onChange={() => toggleLead(lead._id)}
                          sx={{ color:C.textMuted,'&.Mui-checked':{ color:C.accent } }} />
                      </TableCell>

                      {/* Name */}
                      {colConfig.find(c=>c.key==='name')?.visible && (
                        <TableCell sx={{ px:'12px', cursor:'pointer' }} onClick={() => setSelectedLead(lead)}>
                          <Box sx={{ display:'flex', alignItems:'center', gap:'10px' }}>
                            <Avatar sx={{ width:34, height:34, fontSize:'12px', fontWeight:700, bgcolor:C.accentSoft, color:C.accent, fontFamily:"'DM Sans',sans-serif" }}>{getInitials(lead.name)}</Avatar>
                            <Box>
                              <Typography sx={{ fontSize:'13.5px', fontWeight:600, color:C.text, fontFamily:"'DM Sans',sans-serif", lineHeight:1.3 }}>{lead.name}</Typography>
                              <Typography sx={{ fontSize:'12px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif" }}>{lead.email}</Typography>
                            </Box>
                          </Box>
                        </TableCell>
                      )}

                      {/* Phone */}
                      {colConfig.find(c=>c.key==='phone')?.visible && (
                        <TableCell sx={{ px:'12px' }}><PhoneCell user={lead} /></TableCell>
                      )}

                      {/* Project */}
                      {colConfig.find(c=>c.key==='project')?.visible && (
                        <TableCell sx={{ px:'12px' }}>
                          <Typography sx={{ fontSize:'13px', fontWeight:500, color:C.text, fontFamily:"'DM Sans',sans-serif" }}>{lead.project?.name||'—'}</Typography>
                          {lead.developer?.name && <Typography sx={{ fontSize:'11px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif" }}>{lead.developer.name}</Typography>}
                        </TableCell>
                      )}

                      {/* Campaign */}
                      {colConfig.find(c=>c.key==='campaign')?.visible && (
                        <TableCell sx={{ px:'12px' }}>
                          <Typography sx={{ fontSize:'13px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif" }}>{lead.campaign?.CampainName||'—'}</Typography>
                          {lead.campaign?.redirectLink && (
                            <Box sx={{ display:'flex', gap:'4px', mt:'4px' }}>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); navigator.clipboard.writeText(lead.campaign.redirectLink); Notify('success','Link copied','success'); }}
                                sx={{ width:20, height:20, color:C.textMuted,'&:hover':{ color:C.accent } }}><ContentCopyIcon sx={{ fontSize:'13px' }} /></IconButton>
                              <IconButton size="small" onClick={e => { e.stopPropagation(); window.open(lead.campaign.redirectLink,'_blank'); }}
                                sx={{ width:20, height:20, color:C.textMuted,'&:hover':{ color:C.accent } }}><OpenInNewIcon sx={{ fontSize:'13px' }} /></IconButton>
                            </Box>
                          )}
                        </TableCell>
                      )}

                      {/* ✅ Stage — same logic as Admin */}
                      {colConfig.find(c=>c.key==='stage')?.visible && (
                        <TableCell sx={{ px:'12px' }}><StageCell lead={lead} /></TableCell>
                      )}

                      {/* Creation Date */}
                      {colConfig.find(c=>c.key==='createdAt')?.visible && (
                        <TableCell sx={{ px:'12px' }}>
                          {lead.resetcreationdate ? (
                            <Typography sx={{ fontSize:'12px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif", fontStyle:'italic' }}>Not available</Typography>
                          ) : (
                            <>
                              <Typography sx={{ fontSize:'13px', color:C.text, fontFamily:"'DM Sans',sans-serif", fontWeight:500 }}>{lead.date ? moment(lead.date).format('DD/MM/YY') : '—'}</Typography>
                              <Typography sx={{ fontSize:'11px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif" }}>{lead.date ? moment(lead.date).format('HH:mm') : ''}</Typography>
                            </>
                          )}
                        </TableCell>
                      )}

                      {/* Actions */}
                      {colConfig.find(c=>c.key==='actions')?.visible && (
                        <TableCell sx={{ px:'12px' }}>
                          <Box sx={{ display:'flex', gap:'6px', flexWrap:'wrap' }}>
                            {/* Receive */}
                            <Tooltip title={lead.assign ? 'Receive lead' : 'Already received'}>
                              <span>
                                <Button size="small" disabled={!lead.assign}
                                  onClick={() => { setSelectedLeadForRcv(lead); setShowReceiveModal(true); }}
                                  sx={{
                                    fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:'12px',
                                    borderRadius:'7px', textTransform:'none', px:'10px', py:'5px',
                                    bgcolor: lead.assign ? C.warning : C.border,
                                    color:   lead.assign ? '#fff' : C.textMuted,
                                    border:'none',
                                    position:'relative',
                                    '&:hover':{ bgcolor: lead.assign ? '#e68a00' : C.border },
                                    '&.Mui-disabled':{ bgcolor:'#F1F3F9', color:C.textMuted },
                                  }}>
                                  {lead.assign ? 'Receive' : 'Received'}
                                  {lead.assign && <Box sx={{ position:'absolute', top:2, right:2, width:6, height:6, borderRadius:'50%', bgcolor:'#F04438', border:'1px solid #fff' }} />}
                                </Button>
                              </span>
                            </Tooltip>
                            {/* Edit */}
                            <Button size="small" onClick={() => handleShowUpdateLead(lead)}
                              sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:'12px', borderRadius:'7px', textTransform:'none', px:'10px', py:'5px', bgcolor:C.white, color:C.text, border:`1px solid ${C.border}`, '&:hover':{ borderColor:C.accent, color:C.accent, bgcolor:C.accentSoft } }}>
                              Edit
                            </Button>
                            {/* Add to sheet */}
                            <Button size="small" onClick={() => { setSelectedLeadforsheet(lead); setSheetOpen(true); }}
                              sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:600, fontSize:'12px', borderRadius:'7px', textTransform:'none', px:'10px', py:'5px', bgcolor:C.white, color:'#9c27b0', border:'1px solid rgba(156,39,176,0.3)', '&:hover':{ bgcolor:'rgba(156,39,176,0.08)', borderColor:'#9c27b0' } }}>
                              + Sheet
                            </Button>
                          </Box>
                        </TableCell>
                      )}
                    </StyledRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {!loading && displayLeads.length>0 && (
              <Box sx={{ px:2.5, py:1.5, display:'flex', alignItems:'center', justifyContent:'center', borderTop:`1px solid ${C.border}`, flexWrap:'wrap', gap:1.5, bgcolor:'#ece8e8', position:'fixed', bottom:0, borderRadius:'0px 25px 25px 0px', zIndex:10, boxShadow:'0 -2px 8px rgba(16,24,40,0.06)' }}>
                <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                  <Typography sx={{ fontSize:'13px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif", whiteSpace:'nowrap' }}>Rows per page:</Typography>
                  <TextField select value={itemsPerPage} onChange={e => { setItemsPerPage(e.target.value); setCurrentPage(1); loadLeads(1,e.target.value); }} size="small" sx={{ width:72,'& .MuiOutlinedInput-root':{ borderRadius:'8px', fontSize:'13px' } }}>
                    {[10,25,50,100].map(n => <MenuItem key={n} value={n} sx={{ fontFamily:"'DM Sans',sans-serif", fontSize:'13px' }}>{n}</MenuItem>)}
                  </TextField>
                </Box>
                <Typography sx={{ fontSize:'13px', color:C.textMuted, fontFamily:"'DM Sans',sans-serif" }}>
                  Showing <Box component="span" sx={{ fontWeight:700, color:C.text }}>{displayLeads.length}</Box> of <Box component="span" sx={{ fontWeight:700, color:C.text }}>{pagination?.totalItems||displayLeads.length}</Box>
                </Typography>
                {pagination?.NumberOfPages>1 && (
                  <Pagination count={pagination.NumberOfPages} page={currentPage} size="small" showFirstButton showLastButton
                    onChange={(_,v) => { setCurrentPage(v); loadLeads(v,itemsPerPage); window.scrollTo({top:0,behavior:'smooth'}); }}
                    sx={{ '& .MuiPaginationItem-root':{ fontFamily:"'DM Sans',sans-serif", fontSize:'13px', borderRadius:'6px', color:C.textMuted },'& .Mui-selected':{ bgcolor:`${C.accent} !important`, color:'#fff', fontWeight:700 },'& .MuiPaginationItem-root:hover':{ bgcolor:C.accentSoft } }}
                  />
                )}
              </Box>
            )}
          </Box>
        </Box>

        {/* Snackbar bulk actions */}
        {selectedLeads.length>0 && (
          <Snackbar open anchorOrigin={{ vertical:'bottom', horizontal:'center' }}
            message={`${selectedLeads.length} lead(s) selected`}
            action={
              <Box sx={{ display:'flex', gap:1, alignItems:'center' }}>
                <Tooltip title="Return to Team Leader">
                  <IconButton size="small" onClick={handleReturnToTL} sx={{ color:'#fbbf24' }}><ReplyIcon fontSize="small" /></IconButton>
                </Tooltip>
                <Button size="small" onClick={() => setSelectedLeads([])} sx={{ color:'#fff', fontSize:'12px' }}>Clear</Button>
              </Box>
            }
          />
        )}

        {/* Modals */}
        {/* {selectedLead && <ShowClientsActionsAdvanced user={selectedLead} leadid={selectedLead._id} onClose={() => setSelectedLead(null)} />} */}
        {selectedLead && (
  <ShowClientsActionsAdvanced
    user={selectedLead}
    leadid={selectedLead._id}
    onClose={() => setSelectedLead(null)}
    onLeadUpdated={() => loadLeads(currentPage, itemsPerPage)}
  />
)}
        <SalesQuickFilterModal open={quickFilter} onClose={() => setQuickFilter(false)} onApplyFilters={f => { setAppliedFilters(f); setCurrentPage(1); loadLeads(1,itemsPerPage,searchQuery,f); }} initialFilters={appliedFilters} />
        <ShowUserSheet open={sheetOpen} onClose={() => setSheetOpen(false)} lead={selectedLeadforsheet} userlogin={userlogin} />
        <PrivateSheetForUser open={privateSheetOpen} onClose={() => setPrivateSheetOpen(false)} userlogin={userlogin} />

        {/* Confirm Return Dialog */}
        <Dialog open={confirmReturn} onClose={() => setConfirmReturn(false)} maxWidth="xs" fullWidth>
          <DialogTitle sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>Return {selectedLeads.length} lead(s) to Team Leader?</DialogTitle>
          <DialogContent><Alert severity="warning">This action cannot be undone.</Alert></DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmReturn(false)}>Cancel</Button>
            <Button variant="contained" sx={{ bgcolor:C.warning,'&:hover':{ bgcolor:'#e68a00' } }} onClick={executeReturn}>Confirm</Button>
          </DialogActions>
        </Dialog>

        {/* Receive Lead Dialog */}
        <Dialog open={showReceiveModal} onClose={() => { setShowReceiveModal(false); setSelectedLeadForRcv(null); }} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:'16px' } }}>
          <DialogTitle sx={{ bgcolor:C.accent, color:'#fff', fontFamily:"'DM Sans',sans-serif", fontWeight:700, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            Receive Lead
            <IconButton onClick={() => { setShowReceiveModal(false); setSelectedLeadForRcv(null); }} sx={{ color:'#fff' }}><CloseIcon /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt:3 }}>
            {selectedLeadForRcv && (
              <Box>
                <Box sx={{ display:'flex', alignItems:'center', gap:2, mb:3, p:2, bgcolor:C.accentSoft, borderRadius:'10px' }}>
                  <Avatar sx={{ width:48, height:48, bgcolor:C.accent, fontFamily:"'DM Sans',sans-serif", fontWeight:700 }}>{getInitials(selectedLeadForRcv.name)}</Avatar>
                  <Box>
                    <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:'16px', color:C.text }}>{selectedLeadForRcv.name}</Typography>
                    {selectedLeadForRcv.phone && <Typography sx={{ fontFamily:"'DM Sans',sans-serif", fontSize:'13px', color:C.textMuted }}>{selectedLeadForRcv.phone}</Typography>}
                  </Box>
                </Box>
                <Alert severity="info">By receiving this lead, you take full responsibility for following up.</Alert>
              </Box>
            )}
          </DialogContent>
          <DialogActions sx={{ px:3, pb:3 }}>
            <Button onClick={() => { setShowReceiveModal(false); setSelectedLeadForRcv(null); }}>Cancel</Button>
            <Button variant="contained" onClick={handleReceiveLead} disabled={receiveLoading} sx={{ bgcolor:C.accent,'&:hover':{ bgcolor:C.accentDark } }}>
              {receiveLoading ? <CircularProgress size={16} color="inherit" /> : 'Receive Lead'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Edit Lead Dialog */}
        <Dialog open={showUpdateLead} onClose={() => setShowUpdateLead(false)} maxWidth="sm" fullWidth PaperProps={{ sx:{ borderRadius:'16px' } }}>
          <DialogTitle sx={{ fontFamily:"'DM Sans',sans-serif", fontWeight:700, display:'flex', alignItems:'center', justifyContent:'space-between' }}>
            <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
              <EditNoteIcon sx={{ color:C.accent }} />
              Edit Lead
            </Box>
            <IconButton onClick={() => setShowUpdateLead(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
          </DialogTitle>
          <DialogContent sx={{ pt:2 }}>
            <Box sx={{ display:'flex', flexDirection:'column', gap:2.5, mt:1 }}>
              <TextField fullWidth label="Lead Name *" value={leadnameupdate||''} onChange={e => setLeadnameupdate(e.target.value)} size="small" error={!leadnameupdate} helperText={!leadnameupdate ? 'Required' : ''} sx={{ '& .MuiOutlinedInput-root':{ borderRadius:'8px' } }} />
              <Box>
                <Typography sx={{ fontSize:'13px', fontWeight:600, color:C.textMuted, mb:1, fontFamily:"'DM Sans',sans-serif" }}>Phone 2 (optional)</Typography>
                <PhoneInput country="ae" value={phonenumber2||''} onChange={v => setPhonenumber2(v)} preferredCountries={['ae','sa','qa']} enableSearch inputStyle={{ width:'100%', borderRadius:'8px', fontSize:'13px' }} />
              </Box>
              <Box>
                <Typography sx={{ fontSize:'13px', fontWeight:600, color:C.textMuted, mb:1, fontFamily:"'DM Sans',sans-serif" }}>WhatsApp (optional)</Typography>
                <PhoneInput country="ae" value={whatsappnumber||''} onChange={v => setWhatsappnumber(v)} preferredCountries={['ae','sa','qa']} enableSearch inputStyle={{ width:'100%', borderRadius:'8px', fontSize:'13px' }} />
              </Box>
              {resproject?.data && (
                <Autocomplete options={resproject.data} getOptionLabel={o=>o.name||''} value={projectupdatelead||null} onChange={(_,v) => setProjectupdatelead(v)} isOptionEqualToValue={(o,v)=>o._id===v?._id}
                  renderInput={params => <TextField {...params} label="Project *" size="small" error={!projectupdatelead} helperText={!projectupdatelead?'Required':''} sx={{ '& .MuiOutlinedInput-root':{ borderRadius:'8px' } }} />} />
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px:3, pb:3 }}>
            <Button onClick={() => setShowUpdateLead(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleUpdateLead} disabled={loadingUpdate2||!leadnameupdate||!projectupdatelead} sx={{ bgcolor:C.accent,'&:hover':{ bgcolor:C.accentDark } }}>
              {loadingUpdate2 ? <CircularProgress size={16} color="inherit" /> : 'Save Changes'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Settings Drawer */}
        <Drawer anchor="right" open={settingsOpen} onClose={() => setSettingsOpen(false)} PaperProps={{ sx:{ width:360, bgcolor:'#F4F6FB !important', display:'flex', flexDirection:'column' } }}>
          <Box sx={{ px:2.5, py:2, bgcolor:C.white, borderBottom:`1px solid ${C.border}`, display:'flex', alignItems:'center', gap:1 }}>
            <IconButton onClick={() => setSettingsOpen(false)} size="small"><CloseIcon fontSize="small" /></IconButton>
            <Typography sx={{ fontWeight:700, fontSize:'17px', fontFamily:"'DM Sans',sans-serif", color:C.text, flex:1 }}>Column Settings</Typography>
          </Box>
          <Box sx={{ flex:1, overflowY:'auto', px:2.5, pt:2.5, pb:3 }}>
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="cols">
                {provided => (
                  <Box ref={provided.innerRef} {...provided.droppableProps} sx={{ display:'flex', flexDirection:'column', gap:'10px' }}>
                    {colConfig.map((col,index) => (
                      <Draggable key={col.key} draggableId={col.key} index={index}>
                        {(provided,snapshot) => (
                          <Box ref={provided.innerRef} {...provided.draggableProps} sx={{ display:'flex', alignItems:'center', gap:1.5, px:2, py:1.5, bgcolor:snapshot.isDragging?'#E8ECFF':C.white, borderRadius:'14px', border:`1.5px solid ${snapshot.isDragging?C.accent:C.border}`, boxShadow:snapshot.isDragging?'0 8px 24px rgba(26,62,207,0.18)':'0 1px 3px rgba(16,24,40,0.06)' }}>
                            <IconButton size="small" onClick={() => toggleColVisibility(col.key)} sx={{ width:34, height:34, borderRadius:'50%', color:col.visible?C.accent:C.textMuted, bgcolor:col.visible?C.accentSoft:'rgba(123,130,154,0.08)' }}>
                              {col.visible ? <VisibilityIcon sx={{ fontSize:'17px' }} /> : <VisibilityOffIcon sx={{ fontSize:'17px' }} />}
                            </IconButton>
                            <Typography sx={{ flex:1, fontSize:'14px', fontWeight:600, color:col.visible?C.text:C.textMuted, fontFamily:"'DM Sans',sans-serif", textDecoration:col.visible?'none':'line-through' }}>{col.label}</Typography>
                            <Box sx={{ width:48, height:36, bgcolor:'#F0F2F9', borderRadius:'8px', display:'flex', alignItems:'center' }}>
                              <input type="number" value={col.w} onChange={e => updateColWidth(col.key,e.target.value)} onClick={e=>e.stopPropagation()} style={{ width:'100%', height:'100%', border:'none', outline:'none', background:'transparent', textAlign:'center', fontSize:'13px', fontWeight:600, color:C.text, fontFamily:"'DM Sans',sans-serif" }} />
                            </Box>
                            <Box {...provided.dragHandleProps} sx={{ display:'flex', flexDirection:'column', gap:'3px', cursor:'grab', px:'2px' }}>
                              {[0,1,2].map(r=><Box key={r} sx={{ display:'flex', gap:'3px' }}>{[0,1].map(c=><Box key={c} sx={{ width:4, height:4, borderRadius:'50%', bgcolor:C.textMuted, opacity:0.6 }} />)}</Box>)}
                            </Box>
                          </Box>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </Box>
                )}
              </Droppable>
            </DragDropContext>
          </Box>
          <Box sx={{ px:2.5, py:2, borderTop:`1px solid ${C.border}`, bgcolor:C.white, display:'flex', gap:1 }}>
            <Button fullWidth variant="outlined" onClick={() => setColConfig(DEFAULT_COLS)} sx={{ borderColor:C.border, color:C.textMuted, fontFamily:"'DM Sans',sans-serif", textTransform:'none', borderRadius:'10px' }}>Reset</Button>
            <Button fullWidth variant="contained" onClick={() => setSettingsOpen(false)} sx={{ bgcolor:C.accent,'&:hover':{ bgcolor:C.accentDark }, textTransform:'none', fontFamily:"'DM Sans',sans-serif", borderRadius:'10px' }}>Done</Button>
          </Box>
        </Drawer>
      </Box>
    </LocalizationProvider>
  );
};